/**
 * DepartmentManagement.jsx
 * 
 * Admin interface for managing organizational departments.
 * Departments can be assigned to users and used to categorize action item templates.
 * 
 * Features:
 * - View all departments with active/inactive filter
 * - Create new departments with name, description, icon, and color
 * - Edit existing department details
 * - Deactivate/reactivate departments
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/DepartmentManagement.scss';
import { FaEdit, FaInbox, FaPlus, FaSave, FaSitemap, FaSpinner, FaTimes, FaPause, FaPlay, FaSearch } from 'react-icons/fa';
import * as Sentry from '@sentry/react';
import IconPicker from '@/components/Common/IconPicker';
import { FaIcon } from '@/utils/faIconMap';

/**
 * Default color options for departments
 */
const COLOR_OPTIONS = [
    '#3498db', // Blue
    '#2ecc71', // Green
    '#e74c3c', // Red
    '#f39c12', // Orange
    '#9b59b6', // Purple
    '#1abc9c', // Teal
    '#34495e', // Dark Gray
    '#e91e63', // Pink
    '#00bcd4', // Cyan
    '#ff9800'  // Amber
];

/**
 * DepartmentManagement Component
 * Main component for managing organizational departments.
 */
const DepartmentManagement = () => {
    const { apiFetch, user } = useAuth();
    
    // State for departments list
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // State for modal
    const [showModal, setShowModal] = useState(false);
    const [editingDepartment, setEditingDepartment] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        departmentName: '',
        description: '',
        iconClass: 'fa-building',
        color: '#3498db',
        sortOrder: 0
    });
    
    // Validation errors
    const [errors, setErrors] = useState({});

    /**
     * Fetch departments from API
     */
    const fetchDepartments = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`/api/Department?includeInactive=${showInactive}`);
            const data = await response.json();
            
            if (data.success) {
                setDepartments(data.data);
            } else {
                toast.error('Failed to load departments');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching departments:', error);
            toast.error('Error loading departments');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, showInactive]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    const filteredDepartments = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const source = !q
            ? departments
            : departments.filter(dept =>
                (dept.departmentName || '').toLowerCase().includes(q)
                || (dept.description || '').toLowerCase().includes(q)
            );

        return [...source].sort((a, b) => {
            const sortDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            if (sortDiff !== 0) return sortDiff;
            return (a.departmentName || '').localeCompare(b.departmentName || '');
        });
    }, [departments, searchQuery]);

    const stats = useMemo(() => ({
        total: departments.length,
        active: departments.filter(d => d.isActive).length,
        inactive: departments.filter(d => !d.isActive).length
    }), [departments]);

    /**
     * Handle form field change
     */
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear validation error when field is modified
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.departmentName?.trim()) {
            newErrors.departmentName = 'Department name is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Open modal for creating new department
     */
    const handleCreateNew = () => {
        setEditingDepartment(null);
        setFormData({
            departmentName: '',
            description: '',
            iconClass: 'fa-building',
            color: '#3498db',
            sortOrder: departments.length + 1
        });
        setErrors({});
        setShowModal(true);
    };

    /**
     * Open modal for editing existing department
     */
    const handleEdit = (department) => {
        setEditingDepartment(department);
        setFormData({
            departmentName: department.departmentName,
            description: department.description || '',
            iconClass: department.iconClass || 'fa-building',
            color: department.color || '#3498db',
            sortOrder: department.sortOrder
        });
        setErrors({});
        setShowModal(true);
    };

    /**
     * Close modal and reset state
     */
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingDepartment(null);
        setFormData({
            departmentName: '',
            description: '',
            iconClass: 'fa-building',
            color: '#3498db',
            sortOrder: 0
        });
        setErrors({});
    };

    /**
     * Save department (create or update)
     */
    const handleSave = async () => {
        if (!validateForm()) return;
        
        setSaving(true);
        try {
            const payload = {
                ...formData,
                userID: user.userID
            };

            let response;
            if (editingDepartment) {
                // Update existing department
                response = await apiFetch(`/api/Department/${editingDepartment.departmentID}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                // Create new department
                response = await apiFetch('/api/Department', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || (editingDepartment ? 'Department updated' : 'Department created'));
                handleCloseModal();
                fetchDepartments();
            } else {
                toast.error(data.message || 'Failed to save department');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving department:', error);
            toast.error('Error saving department');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Toggle department active status
     */
    const handleToggleActive = async (department) => {
        try {
            const endpoint = department.isActive
                ? `/api/Department/${department.departmentID}?userId=${user.userID}`
                : `/api/Department/${department.departmentID}/reactivate?userId=${user.userID}`;
            
            const response = await apiFetch(endpoint, {
                method: department.isActive ? 'DELETE' : 'POST'
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message);
                fetchDepartments();
            } else {
                toast.error(data.message || 'Failed to update department');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling department status:', error);
            toast.error('Error updating department');
        }
    };

    /**
     * Render department card
     */
    const renderDepartmentCard = (department) => {
        return (
            <div 
                key={department.departmentID}
                className={`department-card ${!department.isActive ? 'inactive' : ''}`}
                style={{ borderLeftColor: department.color || '#3498db' }}
            >
                <div className="department-card-header">
                    <div className="department-icon" style={{ color: department.color || '#3498db' }}>
                        <FaIcon icon={department.iconClass || 'fa-building'} size={24} />
                    </div>

                    <div className="department-info">
                        <h3>{department.departmentName}</h3>
                        {department.description ? (
                            <p className="department-description">{department.description}</p>
                        ) : (
                            <p className="department-description muted">No description provided</p>
                        )}
                    </div>
                </div>

                <div className="department-meta-row">
                    <span className="sort-order">Sort order: {department.sortOrder ?? 0}</span>
                    {!department.isActive && (
                        <span className="badge badge-secondary">Inactive</span>
                    )}
                </div>

                <div className="department-actions">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(department)}
                        title="Edit department"
                    >
                        <FaEdit /> Edit
                    </button>
                    <button
                        className={`btn btn-sm ${department.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                        onClick={() => handleToggleActive(department)}
                        title={department.isActive ? 'Deactivate' : 'Activate'}
                    >
                        {department.isActive ? <FaPause /> : <FaPlay />}
                        {department.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="department-management loading-state">
                <div className="loading-spinner">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading departments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="department-management">
{/* Page Header */}
            <div className="page-header">
                <div className="header-left">
                    <h1>
                        <FaSitemap /> Department Management
                    </h1>
                    <p className="page-description">
                        Manage organizational departments. Departments are used throughout CapTrac to group action items, control access in Board Creation Rules, auto-assign items from email mailboxes, and categorize templates. Add all relevant departments for your organization below.
                    </p>
                </div>
                <div className="header-actions">
                    <button 
                        className="btn btn-primary"
                        onClick={handleCreateNew}
                    >
                        <FaPlus /> Create Department
                    </button>
                </div>
            </div>

            <div className="department-overview-panel">
                <div className="department-stats-row">
                    <div className="stat-card">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value stat-active">{stats.active}</span>
                        <span className="stat-label">Active</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value stat-inactive">{stats.inactive}</span>
                        <span className="stat-label">Inactive</span>
                    </div>
                </div>

                <div className="filter-bar">
                    <div className="search-wrap">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search departments..."
                            aria-label="Search departments"
                        />
                    </div>

                    <label className="checkbox-label">
                        <input 
                            type="checkbox" 
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        <span>Show Inactive Departments</span>
                    </label>

                    <span className="department-count">
                        {filteredDepartments.length} shown
                    </span>
                </div>
            </div>

            {/* Departments Grid */}
            <div className="departments-grid">
                {filteredDepartments.length === 0 ? (
                    <div className="no-departments">
                        <FaInbox className="fa-3x" />
                        <p>No departments found for the current filters.</p>
                        <button className="btn btn-primary" onClick={handleCreateNew}>
                            <FaPlus /> Create Department
                        </button>
                    </div>
                ) : (
                    filteredDepartments.map(dept => renderDepartmentCard(dept))
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-backdrop" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                {editingDepartment ? <FaEdit /> : <FaPlus />}
                                {editingDepartment ? 'Edit Department' : 'Create Department'}
                            </h2>
                            <button className="close-btn" onClick={handleCloseModal}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="departmentName">
                                    Department Name<span className="required">*</span>
                                </label>
                                <input
                                    id="departmentName"
                                    type="text"
                                    className={`form-control ${errors.departmentName ? 'is-invalid' : ''}`}
                                    value={formData.departmentName}
                                    onChange={(e) => handleFieldChange('departmentName', e.target.value)}
                                    placeholder="e.g., Operations, IT, Legal"
                                />
                                {errors.departmentName && (
                                    <div className="invalid-feedback">{errors.departmentName}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    className="form-control"
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    placeholder="Optional description of the department's responsibilities"
                                    rows={3}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Icon</label>
                                    <IconPicker
                                        value={formData.iconClass}
                                        onChange={(icon) => handleFieldChange('iconClass', icon)}
                                        color={formData.color}
                                    />
                                </div>

                                <div className="form-group half">
                                    <label>Color</label>
                                    <div className="color-picker">
                                        {COLOR_OPTIONS.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`color-option ${formData.color === color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => handleFieldChange('color', color)}
                                                title={color}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="sortOrder">Sort Order</label>
                                <input
                                    id="sortOrder"
                                    type="number"
                                    className="form-control"
                                    value={formData.sortOrder}
                                    onChange={(e) => handleFieldChange('sortOrder', parseInt(e.target.value) || 0)}
                                    min="0"
                                />
                                <small className="help-text">Lower numbers appear first in lists</small>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCloseModal}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <FaSpinner className="fa-spin" /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> {editingDepartment ? 'Update' : 'Create'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DepartmentManagement;
